import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

const FAQS = [
  {
    q: "How are your cleaning rates determined?",
    a: "Our rates are based on the size of the premises, the type of cleaning required, and the frequency of service. We provide a free, no-obligation quote after assessing your specific needs — contact us to get started.",
  },
  {
    q: "What areas do you service?",
    a: "We service Christchurch and the wider Canterbury region, including Halswell, Hornby, Riccarton, the CBD, Papanui, Merivale, and surrounding suburbs. Contact us to confirm coverage for your location.",
  },
  {
    q: "What types of cleaning services do you offer?",
    a: "We offer a full range of commercial and specialist cleaning services including office and workplace cleaning, retail and showroom cleaning, industrial and warehouse cleaning, medical and healthcare cleaning, carpet steam cleaning, window cleaning, builders cleans, floor polishing, and more.",
  },
  {
    q: "Do you provide your own cleaning supplies and equipment?",
    a: "Yes. Our teams arrive fully equipped with professional-grade cleaning products and machinery. We can also work with client-supplied products if required for health, safety, or environmental compliance.",
  },
  {
    q: "Are your cleaners trained and background-checked?",
    a: "Absolutely. All staff undergo thorough induction training covering safe chemical handling, equipment operation, health and safety procedures, and site-specific requirements. We maintain a structured supervision system to ensure consistent quality.",
  },
  {
    q: "Are your cleaning products safe for pets and children?",
    a: "We use industry-compliant cleaning products and are happy to accommodate requests for eco-friendly or allergen-conscious alternatives. Please let us know your requirements when booking and we will tailor our product selection accordingly.",
  },
  {
    q: "How can I book a cleaning service?",
    a: "You can request a free quote by visiting our Contact page, calling +64 22 086 6040, or emailing info@clearandclean.co.nz. We'll arrange a site assessment and provide a tailored proposal.",
  },
  {
    q: "What if I'm not satisfied with the cleaning service?",
    a: "Client satisfaction is our priority. If any aspect of the service does not meet your expectations, contact us within 24 hours and we will return to rectify the issue at no additional charge.",
  },
  {
    q: "Do you have a satisfaction guarantee?",
    a: "Yes. We stand behind the quality of our work. If you are not completely satisfied with the result, we will return to address any concerns promptly and at no extra cost to you.",
  },
  {
    q: "What is your cancellation policy?",
    a: "We ask for at least 24 hours' notice for cancellations or rescheduling. This allows us to manage our team's schedule effectively. For recurring contracts, cancellation terms are outlined in your service agreement.",
  },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: "1px solid #e5e7eb" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%", background: "none", border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "20px 0", textAlign: "left", gap: 16,
        }}
      >
        <span style={{ fontWeight: 600, fontSize: 16, color: "#1a1a1a", lineHeight: 1.4 }}>{q}</span>
        <span style={{
          width: 30, height: 30, borderRadius: "50%",
          background: open ? "#1a1a1a" : "#f3f4f6",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0, transition: "background .2s",
        }}>
          <i className={`fa ${open ? "fa-minus" : "fa-plus"}`}
            style={{ fontSize: 11, color: open ? "#fff" : "#6b7280" }} />
        </span>
      </button>
      {open && (
        <p style={{ margin: "0 0 20px", color: "#555", fontSize: 15, lineHeight: 1.75 }}>{a}</p>
      )}
    </div>
  );
}

const FAQ = () => {
  return (
    <>
      <Helmet>
        <title>FAQ — Frequently Asked Questions | Clear &amp; Clean Christchurch</title>
        <meta name="description" content="Find answers to common questions about Clear & Clean's commercial cleaning services in Christchurch — rates, service areas, bookings, guarantees, and more." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.clearandclean.co.nz/faq" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": FAQS.map(({ q, a }) => ({
            "@type": "Question",
            "name": q,
            "acceptedAnswer": { "@type": "Answer", "text": a },
          })),
        })}</script>
      </Helmet>

      {/* Hero */}
      <section
        className="hero-wrap hero-wrap-2"
        style={{ backgroundImage: 'url("/images/service_2.jpg")' }}
        data-stellar-background-ratio="0.5"
      >
        <div className="overlay" />
        <div className="container">
          <div className="row no-gutters slider-text align-items-end">
            <div className="col-md-9 pb-5">
              <p className="breadcrumbs mb-2">
                <span className="mr-2">
                  <Link to="/">Home <i className="fa fa-chevron-right" /></Link>
                </span>
                <span>FAQ <i className="fa fa-chevron-right" /></span>
              </p>
              <h1 className="mb-0 bread">Frequently Asked Questions</h1>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ content */}
      <section className="ftco-section">
        <div className="container">
          <div className="row justify-content-center pb-4">
            <div className="col-md-8 text-center">
              <span className="subheading">FAQ</span>
              <h2>Looking for something?</h2>
              <p style={{ color: "#666", fontSize: 15 }}>
                Browse the most common questions below. Can't find an answer?{" "}
                <Link to="/contact" style={{ color: "#1a1a1a", fontWeight: 600 }}>Contact us</Link>.
              </p>
            </div>
          </div>

          <div className="row justify-content-center">
            <div className="col-lg-8">
              {FAQS.map((item, i) => (
                <FaqItem key={i} q={item.q} a={item.a} />
              ))}
            </div>
          </div>

          <div className="row justify-content-center mt-5">
            <div className="col-md-7 text-center">
              <p style={{ color: "#666", marginBottom: 16 }}>
                Still have questions? We're happy to help.
              </p>
              <Link to="/contact" className="btn btn-dark px-4 py-2 mr-3">
                Contact Us
              </Link>
              <a href="tel:+64220866040" className="btn btn-outline-dark px-4 py-2">
                <i className="fa fa-phone mr-2" />+64 22 086 6040
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default FAQ;
